/**
 * AdminDashboard.jsx  —  v2
 *
 * Uses React Query for data fetching.
 * Layout uses new AdminLayout v2 (components/admin/AdminLayout.jsx).
 *
 * Sections:
 *   1. KPI cards (4): total users, active courses, total revenue, pending issues
 *   2. Activity feed: last 10 events from /admin/logs
 *   3. Quick actions: new course, add user, KVKK queue
 *
 * Charts are deferred to Sprint 5.
 */

import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import AdminLayout from '../components/admin/AdminLayout';
import PageHeader from '../components/admin/PageHeader';
import { useLanguage } from '../contexts/LanguageContext';
import apiClient from '../services/apiClient';
import { formatRelativeTime, formatAbsoluteDateTime, toIsoString } from '../utils/relativeTime';
import {
  Users,
  BookOpen,
  DollarSign,
  AlertTriangle,
  UserPlus,
  PlusCircle,
  ShieldAlert,
  Clock,
  CheckCircle2,
  XCircle,
  Info,
  ArrowRight,
} from 'lucide-react';

// ── Data fetching ─────────────────────────────────────────────────────────────

async function fetchDashboard() {
  const res = await apiClient.get('/admin/dashboard');
  return res.data;
}

async function fetchRecentLogs() {
  const res = await apiClient.get('/admin/logs', { params: { limit: 10 } });
  // Backend may return { logs: [...] } or [...]
  return Array.isArray(res.data) ? res.data : res.data.logs ?? res.data.items ?? [];
}

// ── KPI Card ─────────────────────────────────────────────────────────────────

function KpiCard({ label, value, icon: Icon, color, bg, path, index }) {
  const navigate = useNavigate();
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.07 }}
      onClick={() => path && navigate(path)}
      className={`card-clay p-5 flex flex-col gap-3 ${path ? 'cursor-pointer hover:scale-[1.02]' : ''} transition-transform`}
      data-testid={`kpi-card-${index}`}
    >
      <div
        className="w-11 h-11 rounded-2xl flex items-center justify-center"
        style={{ backgroundColor: bg }}
      >
        <Icon size={22} strokeWidth={2.5} style={{ color }} />
      </div>
      <div>
        <p className="font-display text-2xl font-bold leading-none">{value}</p>
        <p className="text-xs text-[var(--text-muted)] font-semibold mt-1">{label}</p>
      </div>
    </motion.div>
  );
}

// ── Activity Feed Item ────────────────────────────────────────────────────────

function activityIcon(action) {
  const a = (action || '').toLowerCase();
  if (a.includes('create') || a.includes('add') || a.includes('enroll')) return CheckCircle2;
  if (a.includes('delete') || a.includes('remove') || a.includes('reject')) return XCircle;
  if (a.includes('error') || a.includes('fail')) return AlertTriangle;
  return Info;
}

function activityColor(action) {
  const a = (action || '').toLowerCase();
  if (a.includes('create') || a.includes('add') || a.includes('enroll')) return 'text-emerald-500';
  if (a.includes('delete') || a.includes('remove') || a.includes('reject')) return 'text-rose-500';
  if (a.includes('error') || a.includes('fail')) return 'text-amber-500';
  return 'text-sky-500';
}

function ActivityFeedItem({ log, lang, index }) {
  const Icon = activityIcon(log.action);
  const color = activityColor(log.action);
  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.04 }}
      className="flex items-start gap-3 py-2.5 border-b border-[var(--border-light)] last:border-0"
    >
      <Icon size={16} className={`${color} flex-shrink-0 mt-0.5`} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-[var(--text-main)] truncate">
          {log.details || log.description || log.action || '—'}
        </p>
        <p className="text-xs text-[var(--text-muted)]">
          {log.user_name || log.user_email || ''}
        </p>
      </div>
      <time
        dateTime={toIsoString(log.created_at || log.timestamp)}
        title={formatAbsoluteDateTime(log.created_at || log.timestamp, lang)}
        className="text-[10px] text-[var(--text-muted)] flex items-center gap-1 flex-shrink-0 whitespace-nowrap"
      >
        <Clock size={10} />
        {formatRelativeTime(log.created_at || log.timestamp, lang)}
      </time>
    </motion.div>
  );
}

// ── Quick Action ──────────────────────────────────────────────────────────────

function QuickAction({ label, icon: Icon, color, path, testId }) {
  const navigate = useNavigate();
  return (
    <motion.button
      whileHover={{ scale: 1.03 }}
      whileTap={{ scale: 0.97 }}
      onClick={() => navigate(path)}
      className="card-clay p-4 flex items-center gap-3 text-left w-full group"
      data-testid={testId}
    >
      <div
        className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${color}18` }}
      >
        <Icon size={18} strokeWidth={2.5} style={{ color }} />
      </div>
      <span className="text-sm font-bold flex-1">{label}</span>
      <ArrowRight size={14} className="text-[var(--text-muted)] opacity-0 group-hover:opacity-100 transition-opacity" />
    </motion.button>
  );
}

// ── Skeleton loader ───────────────────────────────────────────────────────────

function SkeletonCard() {
  return (
    <div className="card-clay p-5 animate-pulse">
      <div className="w-11 h-11 bg-gray-200 rounded-2xl mb-3" />
      <div className="h-6 bg-gray-200 rounded w-16 mb-1" />
      <div className="h-3 bg-gray-100 rounded w-24" />
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AdminDashboard() {
  const { lang } = useLanguage();

  const {
    data: dashData,
    isLoading: dashLoading,
    isError: dashError,
  } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: fetchDashboard,
  });

  const {
    data: logsData = [],
    isLoading: logsLoading,
  } = useQuery({
    queryKey: ['admin', 'logs', 10],
    queryFn: fetchRecentLogs,
  });

  // KPI definitions
  const kpis = [
    {
      label: lang === 'tr' ? 'Toplam Kullanıcı' : 'Total Users',
      value: dashData?.total_users ?? '—',
      icon: Users,
      color: '#0EA5E9',
      bg: '#E0F2FE',
      path: '/admin/students',
    },
    {
      label: lang === 'tr' ? 'Toplam Kurs' : 'Total Courses',
      value: dashData?.total_courses ?? '—',
      icon: BookOpen,
      color: '#10B981',
      bg: '#D1FAE5',
      path: '/admin/courses',
    },
    {
      label: lang === 'tr' ? 'Toplam Gelir' : 'Total Revenue',
      value: dashData?.total_revenue != null
        ? `${Number(dashData.total_revenue).toLocaleString('tr-TR')} ₺`
        : '—',
      icon: DollarSign,
      color: '#F59E0B',
      bg: '#FEF3C7',
      path: '/admin/accounting',
    },
    {
      label: lang === 'tr' ? 'Bekleyen İşlemler' : 'Pending Issues',
      value: dashData?.pending_issues ?? dashData?.pending_kvkk_count ?? '—',
      icon: AlertTriangle,
      color: '#F43F5E',
      bg: '#FFE4E6',
      path: '/admin/kvkk-requests',
    },
  ];

  const quickActions = [
    {
      label: lang === 'tr' ? 'Yeni Kurs Ekle' : 'Add New Course',
      icon: PlusCircle,
      color: '#10B981',
      path: '/admin/courses',
      testId: 'quick-action-course',
    },
    {
      label: lang === 'tr' ? 'Kullanıcı Ekle' : 'Add User',
      icon: UserPlus,
      color: '#0EA5E9',
      path: '/admin/student-enroll',
      testId: 'quick-action-user',
    },
    {
      label: lang === 'tr' ? 'KVKK Kuyruğu' : 'KVKK Queue',
      icon: ShieldAlert,
      color: '#F43F5E',
      path: '/admin/kvkk-requests',
      testId: 'quick-action-kvkk',
    },
  ];

  return (
    <AdminLayout>
      <div data-testid="admin-dashboard-v2" className="space-y-6">

        <PageHeader
          breadcrumbs={[{ label: lang === 'tr' ? 'Genel Bakış' : 'Dashboard', href: '/admin' }]}
          title={lang === 'tr' ? 'Genel Bakış' : 'Dashboard'}
          subtitle={lang === 'tr' ? 'Akademi özet metrikleri ve son aktiviteler' : 'Academy summary metrics and recent activity'}
        />

        {/* KPI Cards */}
        <section>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {dashLoading
              ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
              : dashError
              ? (
                <div className="col-span-4 card-clay p-6 text-center text-sm text-[var(--text-muted)]">
                  {lang === 'tr'
                    ? 'Dashboard verileri yüklenemedi.'
                    : 'Could not load dashboard data.'}
                </div>
              )
              : kpis.map((kpi, i) => (
                <KpiCard key={kpi.label} {...kpi} index={i} />
              ))}
          </div>
        </section>

        <div className="grid lg:grid-cols-3 gap-6">

          {/* Activity Feed */}
          <div className="lg:col-span-2 card-clay p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display font-bold text-base">
                {lang === 'tr' ? 'Son Etkinlikler' : 'Recent Activity'}
              </h3>
              <a
                href="/admin/logs"
                className="text-xs text-[var(--brand-primary)] font-bold hover:underline flex items-center gap-1"
              >
                {lang === 'tr' ? 'Tümü' : 'See all'}
                <ArrowRight size={12} />
              </a>
            </div>

            {logsLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="flex gap-3 animate-pulse">
                    <div className="w-4 h-4 bg-gray-200 rounded-full mt-0.5" />
                    <div className="flex-1">
                      <div className="h-3 bg-gray-200 rounded w-3/4 mb-1" />
                      <div className="h-2.5 bg-gray-100 rounded w-1/2" />
                    </div>
                  </div>
                ))}
              </div>
            ) : logsData.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)] text-center py-8">
                {lang === 'tr' ? 'Henüz etkinlik yok.' : 'No activity yet.'}
              </p>
            ) : (
              <div>
                {logsData.slice(0, 10).map((log, i) => (
                  <ActivityFeedItem key={log._id || log.id || i} log={log} lang={lang} index={i} />
                ))}
              </div>
            )}
          </div>

          {/* Quick Actions */}
          <div className="card-clay p-5">
            <h3 className="font-display font-bold text-base mb-4">
              {lang === 'tr' ? 'Hızlı İşlemler' : 'Quick Actions'}
            </h3>
            <div className="space-y-2">
              {quickActions.map((action) => (
                <QuickAction key={action.testId} {...action} />
              ))}
            </div>

            {/* Role distribution mini widget */}
            {dashData?.users_by_role && (
              <div className="mt-5 pt-4 border-t border-[var(--border-light)]">
                <p className="text-xs font-bold text-[var(--text-muted)] uppercase tracking-wider mb-3">
                  {lang === 'tr' ? 'Rol Dağılımı' : 'User Roles'}
                </p>
                <div className="space-y-2">
                  {Object.entries(dashData.users_by_role).map(([role, count]) => (
                    <div key={role} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                          role === 'admin' || role === 'superadmin'
                            ? 'bg-rose-500'
                            : role === 'instructor'
                            ? 'bg-sky-500'
                            : 'bg-emerald-500'
                        }`} />
                        <span className="text-xs font-semibold capitalize">{role}</span>
                      </div>
                      <span className="font-display font-bold text-sm">{count}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
